import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Dialog, IconButton, Typography } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { Fragment } from 'react';
import { YellowButton } from '@/components/button/button';
import { experienceTypeLabel } from '@/constants/experience-type-label';
import { ExperienceType } from '@/generated/graphql';
import { modalExperiences } from './constants';

const CloseButton = styled(IconButton)`
  position: absolute;
  z-index: 99;
  right: 5px;
  top: 5px;
  color: ${({ theme }) => theme.palette.common.midnight};
`;

const Title = styled(Typography)`
  padding: 0 20px;
  margin-top: 20px;
  font-family: var(--font-cooper-light);
  font-style: normal;
  font-weight: 300;
  font-size: 50px;
  line-height: 109.3%;
  color: ${({ theme }) => theme.palette.common.midnight};
  margin-bottom: 32px;
`;
const ContentDiv = styled(DialogContent)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const Steps = styled.div`
  padding: 20px;
  margin-top: 32px;
  margin-bottom: 60px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const Step = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const StepName = styled(Typography)`
  font-family: var(--font-work-sans);
  width: 183px;
  font-weight: 600;
  font-size: 18px;
  line-height: 148%;
  color: #000000;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const ImageDiv = styled.div`
  height: 103px;
  width: 99px;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;
const StepIcon = styled.img`
  height: auto;
  width: 100%;
`;
const Text = styled(Typography)`
  padding: 0 20px;
  font-family: var(--font-work-sans);
  width: 100%;
  font-size: 18px;
  line-height: 160%;
  align-items: center;
  color: ${({ theme }) => theme.palette.common.midnight};
`;
const IconDiv = styled.div`
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;
const Icon = styled.img`
  width: 65px;
  height: 65px;
  object-fit: cover;
`;
const Dots = styled.img`
  height: 28px;
  width: 6px;
  margin-left: 50px;
`;
const BottomDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 60px;
  padding-top: 60px;
  row-gap: 30px;
  width: 100%;
  background: #231727;

  ${Text} {
    font-size: 22px;
    width: 70%;
    line-height: 127.3%;
    text-align: center;
    color: #ffffff;
  }
`;
export type ExperienceModalProps = {
  open: boolean;
  onClose: () => void;
  type?: Exclude<ExperienceType, ExperienceType.Undefined>;
};

const ExperiencesModal = ({ open, onClose, type }: ExperienceModalProps) => {
  if (!type) {
    return null;
  }
  const expObj = modalExperiences[type] || {};
  const typeLabel = experienceTypeLabel[type || ExperienceType.Video];
  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      <ContentDiv>
        <Global
          styles={css`
            .MuiDialogContent-root {
              padding: 0 !important;
            }
          `}
        />
        <IconDiv>
          <Icon src={expObj.mainIcon} alt={'icon'} />
        </IconDiv>
        <Title>{expObj.heading}</Title>
        <Text>{expObj.detail}</Text>
        <Steps>
          {expObj?.steps?.map((m, index) => (
            <Fragment key={m.name}>
              <Step>
                <ImageDiv>
                  <StepIcon src={m.icon} alt={m.name}></StepIcon>
                </ImageDiv>
                <StepName>{m.name}</StepName>
              </Step>
              {index != expObj.steps!.length - 1 ? (
                <Dots src="/discover/experiences/modal/dots.png" alt="dots" />
              ) : null}
            </Fragment>
          ))}
        </Steps>

        <BottomDiv>
          <Text>{`Ready to try a ${typeLabel} experience?`}</Text>
          <YellowButton label={`Explore ${typeLabel} experiences`} />
        </BottomDiv>
      </ContentDiv>
    </Dialog>
  );
};

export default ExperiencesModal;
